import React, { useEffect, useMemo, useState } from 'react';
import {
    AccountsPageQuery,
    AccountsSortableFields,
    ColumnOrder,
    useAccountsPageLazyQuery,
} from '../../../../../services';
import {
    CTableWrapper,
    DBlock,
    DeleteIcon,
    DPage,
    DPageBody,
    DPaginator,
    DTextField,
    EditIcon,
    SortedOrder,
    usePaginator,
} from '../../../../dum';
import { SDeleteAccountDialog, SUpsertAccountDialog } from '../../../../smart';
import { Box, IconButton, Link, Typography } from '@mui/material';
import {
    COLUMN_LABELS,
    FIELD_LABELS,
    shortLabel,
    useDebounce,
    useString,
} from '../../../../../helpers';
import { useNavigate } from 'react-router-dom';
import { navigateToAccountPage } from '../../../../../services';

type AccountRow = AccountsPageQuery['paginatedAccounts']['docs'][number];

interface IAccountsPageProps {
    // Scope the list to client accounts (Clientes page). Defaults to the full
    // account list (Cuentas page).
    isClient?: boolean;
    // Read-only mode: hides create/edit/delete affordances. Used by Ventas,
    // which cannot mutate accounts (mutations are ADMIN-gated).
    viewOnly?: boolean;
    title?: string;
    // Namespaces the persisted filter / paginator state so two mountings
    // (Cuentas vs Clientes) don't share each other's saved page/filter.
    storageKeyPrefix?: string;
    // Where the name link / "Ver" action navigates. Defaults to the admin
    // account page; Clientes overrides this to its own /clients/:id route.
    navigateToItem?: (accountId: number) => void;
}

export function AccountsPage({
    isClient = false,
    viewOnly = false,
    title = 'Cuentas',
    storageKeyPrefix = 'AccountsPage',
    navigateToItem,
}: IAccountsPageProps = {}) {
    const navigate = useNavigate();
    const [query, { data, previousData, loading }] =
        useAccountsPageLazyQuery();
    const [upsertOpen, setUpsertOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [accountId, setAccountId] = useState<number | null>(null);

    const [sortedIndex, setSortedIndex] = useState<number | null>(null);
    const [sortedOrder, setSortedOrder] = useState<SortedOrder>(null);

    const goToItem = useMemo(() => {
        return (
            account: AccountsPageQuery['paginatedAccounts']['docs'][number],
        ) => {
            if (navigateToItem) {
                navigateToItem(account.id);
            } else {
                navigateToAccountPage({ navigate, accountId: account.id });
            }
        };
    }, [navigate, navigateToItem]);

    const [filter, setFilter] = useString({
        KEY: storageKeyPrefix + '_Filter',
    });

    const debouncedFilter = useDebounce(filter, 400);

    const { setSkip, skip, take } = usePaginator(
        {
            KEY: storageKeyPrefix + '_Paginator',
        },
        [debouncedFilter],
    );

    useEffect(() => {
        void query({
            variables: {
                skip: skip,
                take: take,
                filter: debouncedFilter,
                isClient: isClient ? true : null,
                sortField:
                    sortedIndex === 0
                        ? AccountsSortableFields.Name
                        : sortedIndex === 1
                        ? AccountsSortableFields.Abbreviation
                        : null,
                sortOrder:
                    sortedOrder === 'asc'
                        ? ColumnOrder.Asc
                        : sortedOrder === 'desc'
                        ? ColumnOrder.Desc
                        : null,
            },
        });
    }, [query, skip, take, debouncedFilter, sortedOrder, sortedIndex, isClient]);

    const accounts =
        data?.paginatedAccounts.docs ||
        previousData?.paginatedAccounts.docs ||
        [];

    const count =
        data && data?.paginatedAccounts.count >= 0
            ? data?.paginatedAccounts.count
            : previousData?.paginatedAccounts.count;

    const handleEditAccount = useMemo(() => {
        return (
            account: AccountsPageQuery['paginatedAccounts']['docs'][number],
        ) => {
            setAccountId(account.id);
            setUpsertOpen(true);
        };
    }, []);

    const handleDeleteAccount = useMemo(() => {
        return (
            account: AccountsPageQuery['paginatedAccounts']['docs'][number],
        ) => {
            setAccountId(account.id);
            setDeleteOpen(true);
        };
    }, []);

    return (
        <DPage>
            <DPageBody disableGutters>
                <DBlock>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>{title}</Typography>
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <DTextField
                                label={'Filtro'}
                                helperText={'Nombre o abreviatura.'}
                                value={filter}
                                onChange={(value) => {
                                    setFilter(value || '');
                                }}
                                hideHelperTextBelowLg
                            />
                        </Box>
                        <DPaginator skip={skip} take={take} setSkip={setSkip} count={count} disableGutters />
                    </Box>
                    <CTableWrapper
                        onRowDoubleClick={(account) => {
                            if (viewOnly) {
                                goToItem(account);
                            } else {
                                handleEditAccount(account);
                            }
                        }}
                        sortedIndex={sortedIndex}
                        setSortedIndex={setSortedIndex}
                        sortedOrder={sortedOrder}
                        setSortedOrder={setSortedOrder}
                        contextItems={[
                            {
                                title: shortLabel(COLUMN_LABELS.view),
                                callback: (account) => {
                                    goToItem(account);
                                },
                            },
                            ...(!viewOnly
                                ? [
                                      {
                                          title: shortLabel(COLUMN_LABELS.edit),
                                          callback: (account: AccountRow) => {
                                              handleEditAccount(account);
                                          },
                                      },
                                      {
                                          title: shortLabel(
                                              COLUMN_LABELS.delete,
                                          ),
                                          disable: (account: AccountRow) => {
                                              return !account.is_deletable;
                                          },
                                          callback: (account: AccountRow) => {
                                              handleDeleteAccount(account);
                                          },
                                      },
                                  ]
                                : []),
                        ]}
                        items={[
                            {
                                title: shortLabel(FIELD_LABELS.name),
                                cellGrow: 45,
                                sortable: true,
                                content: (account) => {
                                    return (
                                        <Link
                                            component="button"
                                            type="button"
                                            underline="hover"
                                            color="primary"
                                            sx={{ textAlign: 'left' }}
                                            onClick={(e: React.MouseEvent) => {
                                                // Keep the navigation link independent
                                                // from the row's implicit double-click
                                                // editor. It goes to the account view
                                                // page on the first click.
                                                e.stopPropagation();
                                                goToItem(account);
                                            }}
                                        >
                                            {account.name}
                                        </Link>
                                    );
                                },
                            },
                            {
                                title: shortLabel(FIELD_LABELS.abbreviation),
                                cellGrow: 45,
                                sortable: true,
                                content: (account) => {
                                    return account.abbreviation;
                                },
                            },
                            {
                                title: shortLabel(COLUMN_LABELS.account_type),
                                cellGrow: 25,
                                content: (account) => {
                                    const statuses: string[] = [];
                                    if (account.is_client) {
                                        statuses.push('Cliente');
                                    }
                                    if (account.is_own) {
                                        statuses.push('Cuenta propia');
                                    }

                                    if (account.is_supplier) {
                                        statuses.push('Proveedor');
                                    }
                                    return statuses.join(', ');
                                },
                            },
                            {
                                title: shortLabel(FIELD_LABELS.updated_by_id),
                                cellGrow: 30,
                                content: (account) => {
                                    return account.updated_by?.fullname || '—';
                                },
                            },
                            ...(!viewOnly
                                ? [
                            {
                                title: '',
                                textAlign: 'center' as const,
                                content: (account: AccountRow) => {
                                    return (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 0.5, justifyContent: 'center',
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                sx={{ bgcolor: 'rgba(34,197,94,0.15)', boxShadow: 2, borderRadius: '50%' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditAccount(account);
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                sx={{ bgcolor: 'rgba(239,68,68,0.15)', boxShadow: 2, borderRadius: '50%' }}
                                                disabled={!account.is_deletable}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteAccount(
                                                        account,
                                                    );
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    );
                                },
                            },
                                  ]
                                : []),
                        ]}
                        data={accounts}
                        loading={loading}
                    />
                </DBlock>
            </DPageBody>
            {!viewOnly ? (
                <>
                    <SUpsertAccountDialog
                        open={upsertOpen}
                        setOpen={setUpsertOpen}
                        accountId={accountId}
                    />
                    <SDeleteAccountDialog
                        open={deleteOpen}
                        setOpen={setDeleteOpen}
                        accountId={accountId}
                    />
                </>
            ) : null}
        </DPage>
    );
}
